import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';

// Initialize persistence
// This Promise might not resolve immediately but it sets the persistence for future sign-ins
setPersistence(auth, browserLocalPersistence).catch(error => {
  console.error("Auth persistence error:", error);
});

// Register a new user
export const registerUser = async (email, displayName, password, referralCode = null) => {
  try {
    console.log("🚀 Starting user registration for:", email);

    // Create user with Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log("✅ User created in Firebase Auth:", userCredential.user.uid);

    // Update profile with display name
    await updateProfile(userCredential.user, {
      displayName: displayName
    });
    console.log("✅ Profile updated with display name");

    // Send email verification
    try {
      await sendEmailVerification(userCredential.user);
      console.log("📧 Verification email sent");
    } catch (verifyErr) {
      console.warn("⚠️ Failed to send verification email:", verifyErr);
    }

    // Generate a referral code
    const generatedReferralCode = generateReferralCode();

    // Check if this is admin email
    const isAdmin = email.toLowerCase() === 'admin@filezen.com';

    // Referral handling
    let initialCredits = isAdmin ? 10000 : 0;
    let referredBy = null;
    let usedReferralCode = null;

    if (referralCode) {
      try {
        const code = (referralCode || '').toUpperCase().trim();
        // Prefer referralCodes mapping collection (openly readable per rules)
        const refCodeSnap = await getDoc(doc(db, 'referralCodes', code));
        if (refCodeSnap.exists()) {
          const refData = refCodeSnap.data() || {};
          referredBy = refData.userId || null;
          usedReferralCode = code;
          initialCredits = Math.max(initialCredits, 50); // grant 50 credits for valid referral
          console.log('✅ Referral code valid via referralCodes, granting 50 credits, referredBy:', referredBy);
        } else {
          console.warn('❌ Invalid referral code provided (not found in referralCodes)');
        }
      } catch (e) {
        console.error('Error verifying referral code:', e);
      }
    }

    // Create user document in Firestore
    const userDoc = {
      uid: userCredential.user.uid,
      email: email,
      displayName: displayName,
      credits: initialCredits, // grant based on referral/admin
      isAdmin: isAdmin, // Set admin flag
      emailVerified: !!userCredential.user.emailVerified,
      signupBonusGranted: isAdmin ? true : false,
      referralCode: generatedReferralCode,
      referredBy: referredBy || null,
      usedReferralCode: usedReferralCode || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Save user to Firestore
    await setDoc(doc(db, "users", userCredential.user.uid), userDoc);
    console.log("✅ User document created in Firestore");

    // Register mapping for this user's referral code → userId
    try {
      await setDoc(doc(db, 'referralCodes', generatedReferralCode), {
        code: generatedReferralCode,
        userId: userCredential.user.uid,
        createdAt: serverTimestamp()
      }, { merge: true });
      console.log('✅ referralCodes mapping created for new user');
    } catch (e) {
      console.warn('⚠️ Failed to create referralCodes mapping:', e);
    }

    return {
      user: {
        id: userCredential.user.uid,
        email: userCredential.user.email,
        display_name: userCredential.user.displayName,
        credits: userDoc.credits,
        isAdmin: userDoc.isAdmin,
        referral_code: userDoc.referralCode,
        emailVerified: !!userCredential.user.emailVerified
      }
    };
  } catch (error) {
    console.error("❌ Registration error:", error.code, error.message);

    // Provide more user-friendly error messages
    if (error.code === 'auth/email-already-in-use') {
      throw new Error("This email is already registered. Please use a different email or try logging in.");
    } else if (error.code === 'auth/weak-password') {
      throw new Error("Password is too weak. Please use a stronger password with at least 6 characters.");
    } else if (error.code === 'auth/invalid-email') {
      throw new Error("The email address is not valid. Please check and try again.");
    } else if (error.code === 'auth/operation-not-allowed') {
      throw new Error("Email/password accounts are not enabled. Please contact support.");
    } else {
      throw new Error(error.message || "Failed to register. Please try again later.");
    }
  }
};

// Login user
export const loginUser = async (email, password) => {
  try {
    console.log("🚀 Attempting login for:", email);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log("✅ User authenticated:", userCredential.user.uid);

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log("✅ User document retrieved from Firestore");

      // Ensure referralCodes mapping exists for existing users
      try {
        const code = (userData.referralCode || '').toUpperCase();
        if (code) {
          const mapSnap = await getDoc(doc(db, 'referralCodes', code));
          if (!mapSnap.exists()) {
            await setDoc(doc(db, 'referralCodes', code), {
              code,
              userId: userCredential.user.uid,
              createdAt: serverTimestamp()
            }, { merge: true });
            console.log('✅ Backfilled referralCodes mapping for existing user');
          }
        }
      } catch (e) {
        console.warn('⚠️ Failed to ensure referralCodes mapping on login:', e);
      }

      return {
        user: {
          id: userCredential.user.uid,
          email: userCredential.user.email,
          display_name: userCredential.user.displayName || userData.displayName,
          credits: userData.credits,
          referral_code: userData.referralCode,
          isAdmin: userData.isAdmin || false,
          emailVerified: !!userCredential.user.emailVerified
        }
      };
    } else {
      console.warn("⚠️ User document not found in Firestore, creating basic document");
      // Create a basic user document if it doesn't exist
      const basicUserData = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName || email.split('@')[0],
        credits: 0,
        referralCode: generateReferralCode(),
        isAdmin: false,
        emailVerified: !!userCredential.user.emailVerified,
        signupBonusGranted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, "users", userCredential.user.uid), basicUserData);
      console.log("✅ Created missing user document in Firestore");

      // Create mapping for referral code
      try {
        await setDoc(doc(db, 'referralCodes', basicUserData.referralCode), {
          code: basicUserData.referralCode,
          userId: userCredential.user.uid,
          createdAt: serverTimestamp()
        }, { merge: true });
        console.log('✅ referralCodes mapping created for user on first login');
      } catch (e) {
        console.warn('⚠️ Failed to create referralCodes mapping on first login:', e);
      }

      return {
        user: {
          id: userCredential.user.uid,
          email: userCredential.user.email,
          display_name: userCredential.user.displayName || basicUserData.displayName,
          credits: basicUserData.credits,
          referral_code: basicUserData.referralCode,
          isAdmin: false,
          emailVerified: !!userCredential.user.emailVerified
        }
      };
    }
  } catch (error) {
    console.error("❌ Login error:", error.code, error.message);

    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error("Invalid email or password. Please try again.");
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error("Too many unsuccessful login attempts. Please try again later or reset your password.");
    } else if (error.code === 'auth/user-disabled') {
      throw new Error("This account has been disabled. Please contact support.");
    } else {
      throw new Error(error.message || "Failed to login. Please try again later.");
    }
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
    console.log("✅ User signed out successfully");
    return true;
  } catch (error) {
    console.error("❌ Logout error:", error);
    throw new Error("Failed to logout. Please try again.");
  }
};

// Get current user data
export const getCurrentUser = async () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();

      if (user) {
        try {
          console.log("✅ Current user found:", user.uid);
          const userDoc = await getDoc(doc(db, "users", user.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Ensure referralCodes mapping exists for existing users
            try {
              const code = (userData.referralCode || '').toUpperCase();
              if (code) {
                const mapSnap = await getDoc(doc(db, 'referralCodes', code));
                if (!mapSnap.exists()) {
                  await setDoc(doc(db, 'referralCodes', code), {
                    code,
                    userId: user.uid,
                    createdAt: serverTimestamp()
                  }, { merge: true });
                  console.log('✅ Backfilled referralCodes mapping in getCurrentUser');
                }
              }
            } catch (e) {
              console.warn('⚠️ Failed to ensure referralCodes mapping in getCurrentUser:', e);
            }

            resolve({
              id: user.uid,
              email: user.email,
              display_name: user.displayName || userData.displayName,
              credits: userData.credits,
              referral_code: userData.referralCode,
              isAdmin: userData.isAdmin || false,
              emailVerified: !!user.emailVerified
            });
          } else {
            console.warn("⚠️ User authenticated but no document in Firestore, creating user document");
            // Create a user document for Google sign-in users
            const generatedReferralCode = generateReferralCode();
            const isAdmin = user.email.toLowerCase() === 'admin@filezen.com';
            const initialCredits = isAdmin ? 10000 : 100; // Give 100 credits to new Google users

            const basicUserData = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              credits: initialCredits,
              referralCode: generatedReferralCode,
              isAdmin: isAdmin,
              emailVerified: !!user.emailVerified,
              signupBonusGranted: true, // Auto-grant signup bonus for Google users
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            await setDoc(doc(db, "users", user.uid), basicUserData);
            console.log("✅ Created user document in Firestore for Google user with signup bonus");

            // Create mapping for referral code
            try {
              await setDoc(doc(db, 'referralCodes', generatedReferralCode), {
                code: generatedReferralCode,
                userId: user.uid,
                createdAt: serverTimestamp()
              }, { merge: true });
              console.log('✅ referralCodes mapping created for Google user');
            } catch (e) {
              console.warn('⚠️ Failed to create referralCodes mapping for Google user:', e);
            }

            // Record the signup bonus transaction
            try {
              const { addDoc, collection, query, where, getDocs } = await import('firebase/firestore');

              // Check if signup bonus transaction already exists
              const existingTransactionQuery = query(
                collection(db, 'transactions'),
                where('userId', '==', user.uid),
                where('transactionType', '==', 'signup_bonus')
              );
              const existingTransactions = await getDocs(existingTransactionQuery);

              if (existingTransactions.empty) {
                await addDoc(collection(db, 'transactions'), {
                  userId: user.uid,
                  transactionType: 'signup_bonus',
                  amount: initialCredits,
                  price: 0,
                  currency: 'CREDITS',
                  paymentProvider: 'system',
                  paymentId: `signup_bonus_google_getcurrentuser_${user.uid}`,
                  status: 'completed',
                  visible: true,
                  createdAt: serverTimestamp()
                });
                console.log('✅ Signup bonus transaction recorded for Google user in getCurrentUser');
              } else {
                console.log('✅ Signup bonus transaction already exists for Google user in getCurrentUser');
              }
            } catch (e) {
              console.warn('⚠️ Failed to record signup bonus transaction for Google user in getCurrentUser:', e);
            }

            resolve({
              id: user.uid,
              email: user.email,
              display_name: user.displayName || basicUserData.displayName,
              credits: basicUserData.credits,
              referral_code: basicUserData.referralCode,
              isAdmin: isAdmin,
              emailVerified: !!user.emailVerified
            });
          }
        } catch (error) {
          console.error("❌ Error getting user data:", error);
          // Return basic user info from Auth if Firestore fails
          resolve({
            id: user.uid,
            email: user.email,
            display_name: user.displayName || user.email.split('@')[0],
            credits: 0, // Default to 0 to avoid granting credits before verification
            isAdmin: false,
            emailVerified: !!user.emailVerified
          });
        }
      } else {
        console.log("ℹ️ No user currently signed in");
        resolve(null);
      }
    });
  });
};

// Generate a random referral code
const generateReferralCode = () => {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
};